import jsPDF from "jspdf";
import "jspdf-autotable";
import { startOfDay, startOfWeek, startOfMonth, startOfYear, subDays, subWeeks, subMonths, subYears, format, isSameDay,
  isSameWeek,
  isSameMonth,
  isSameYear,
  } from "date-fns";


/**
 * 📈 Process monthly counts for charts
 */
export const processMonthlyData = (data, dateField) => {
  const monthlyCounts = new Array(12).fill(0);
  data.forEach((item) => {
    const month = new Date(item[dateField]).getMonth();
    monthlyCounts[month]++;
  });
  return monthlyCounts;
};

/**
 * 🧹 Format a dynamic file name for exports
 */
export const formatFileName = (type) => {
  const timestamp = new Date().toISOString().replace(/[:.]/g, "-");
  return `report_${type}_${timestamp}`;
};



export const processWorkOrderComparison = (data, dateField, view = "month") => {
  const now = new Date();
  let currentFilter, previousFilter, labelFormat;

  switch (view) {
    case "day":
      currentFilter = date => isSameDay(date, now);
      previousFilter = date => isSameDay(date, subDays(now, 1));
      labelFormat = "HH"; // Hour (00–23)
      break;
    case "week":
      currentFilter = date => isSameWeek(date, now, { weekStartsOn: 1 });
      previousFilter = date => isSameWeek(date, subWeeks(now, 1), { weekStartsOn: 1 });
      labelFormat = "EEE"; // Mon, Tue...
      break;
    case "month":
      currentFilter = date => isSameMonth(date, now);
      previousFilter = date => isSameMonth(date, subMonths(now, 1));
      labelFormat = "d"; // Day of month (1–31)
      break;
    case "year":
      currentFilter = date => isSameYear(date, now);
      previousFilter = date => isSameYear(date, subYears(now, 1));
      labelFormat = "MMM"; // Jan–Dec
      break;
    default:
      return { labels: [], current: [], previous: [] };
  }

  const countByLabel = (orders, filterFn) => {
    const counts = {};
    for (const order of orders) {
      const date = new Date(order[dateField]);
      if (!filterFn(date)) continue;
      const key = format(date, labelFormat);
      counts[key] = (counts[key] || 0) + 1;
    }
    return counts;
  };

  const currentCounts = countByLabel(data, currentFilter);
  const previousCounts = countByLabel(data, previousFilter);

  let allLabels = Array.from(new Set([
    ...Object.keys(currentCounts),
    ...Object.keys(previousCounts)
  ]));

  // 🔧 Correct sorting based on view
  let sortedLabels;

  if (view === "month" || view === "day") {
    sortedLabels = allLabels.sort((a, b) => Number(a) - Number(b)); // Numeric sort
  } else if (view === "year") {
    const monthOrder = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
    sortedLabels = allLabels.sort((a, b) => monthOrder.indexOf(a) - monthOrder.indexOf(b));
  } else if (view === "week") {
    const dayOrder = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
    sortedLabels = allLabels.sort((a, b) => dayOrder.indexOf(a) - dayOrder.indexOf(b));
  } else {
    sortedLabels = allLabels.sort();
  }

  return {
    labels: sortedLabels,
    current: sortedLabels.map(label => currentCounts[label] || 0),
    previous: sortedLabels.map(label => previousCounts[label] || 0),
  };
};



export const processWorkOrderTypeComparison = (data, dateField, view = "month") => {
  const now = new Date();
  let currentFilter, previousFilter;

  switch (view) {
    case "day":
      currentFilter = date => isSameDay(date, now);
      previousFilter = date => isSameDay(date, subDays(now, 1));
      break;
    case "week":
      currentFilter = date => isSameWeek(date, now, { weekStartsOn: 1 });
      previousFilter = date => isSameWeek(date, subWeeks(now, 1), { weekStartsOn: 1 });
      break;
    case "month":
      currentFilter = date => isSameMonth(date, now);
      previousFilter = date => isSameMonth(date, subMonths(now, 1));
      break;
    case "year":
      currentFilter = date => isSameYear(date, now);
      previousFilter = date => isSameYear(date, subYears(now, 1));
      break;
    default:
      return { labels: [], current: [], previous: [] };
  }

  const countTypes = (orders, filterFn) => {
    const counts = {};
    for (const order of orders) {
      const date = new Date(order[dateField]);
      if (!filterFn(date)) continue;
      const type = order.type_name || "Unknown Type";
      counts[type] = (counts[type] || 0) + 1;
    }
    return counts;
  };

  const currentCounts = countTypes(data, currentFilter);
  const previousCounts = countTypes(data, previousFilter);

  // Combine all types and sort by max count
  const allTypes = Array.from(new Set([
    ...Object.keys(currentCounts),
    ...Object.keys(previousCounts)
  ]));

  const sortedTopTypes = allTypes
    .map(type => ({
      type,
      total: (currentCounts[type] || 0) + (previousCounts[type] || 0)
    }))
    .sort((a, b) => b.total - a.total)
    .slice(0, 5);

  const labels = sortedTopTypes.map(t => t.type);

  return {
    labels,
    current: labels.map(label => currentCounts[label] || 0),
    previous: labels.map(label => previousCounts[label] || 0)
  };
};


/**
 * 🛠 Export data to CSV or PDF
 */
export const exportReport = (rawData, format, type, filter = {}) => {
  if (!rawData || rawData.length === 0) {
    alert("No data available to export!");
    return;
  }

  const fileName = formatFileName(type);

  const dateField = type === "violations" ? "violated_at" :
                    type === "work_orders" ? "created_at" :
                    "requested_at";

  const filteredData = rawData.filter((item) => {
    const itemDate = new Date(item[dateField]);
    const start = filter.startDate ? new Date(filter.startDate) : null;
    const end = filter.endDate ? new Date(filter.endDate) : null;
    return (!start || itemDate >= start) && (!end || itemDate <= end);
  });

  if (filteredData.length === 0) {
    alert("No records found in the selected date range.");
    return;
  }

  if (format === "PDF") {
    const doc = new jsPDF({ orientation: "landscape", unit: "mm", format: "tabloid" });
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(16);
    doc.text('Report', 14, 20);
    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    doc.text(`Generated on: ${new Date().toLocaleString()}`, 14, 28);

    const headers = Object.keys(filteredData[0] || {}).map(h =>
      h.replace(/_/g, " ").replace(/\b\w/g, l => l.toUpperCase())
    );

    const body = filteredData.map(item =>
      headers.map(h => {
        const rawKey = h.toLowerCase().replace(/ /g, "_");
        const val = item[rawKey];
        return val !== undefined && val !== null ? String(val) : "";
      })
    );

    doc.autoTable({
      head: [headers],
      body: body,
      styles: { font: 'courier', fontSize: 9, cellPadding: 3, overflow: 'linebreak' },
      headStyles: { fillColor: [60, 60, 60], textColor: [255, 255, 255] },
      alternateRowStyles: { fillColor: [245, 245, 245] },
      startY: 35,
      margin: { left: 14, right: 14 },
      tableWidth: 'auto',
      theme: 'grid',
    });

    doc.save(`${fileName}.pdf`);
  } else if (format === "CSV") {
    const headers = Object.keys(filteredData[0]);
    const csvRows = [headers.join(",")];

    filteredData.forEach(item => {
      const values = headers.map(header => `"${String(item[header] || "").replace(/"/g, '""')}"`);
      csvRows.push(values.join(","));
    });

    const blob = new Blob([csvRows.join("\n")], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);
    link.setAttribute("href", url);
    link.setAttribute("download", `${fileName}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }
};
